import React, { useState, useEffect } from 'react';
import b2bService from '../../services/b2bService';
import './B2bCorporateBillingPage.css';
import './B2bPortalOrderHistoryPage.css';

const B2bSpecimenLogisticsPage = () => {
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [scanBarcode, setScanBarcode] = useState('');
  const [addingItem, setAddingItem] = useState(false);

  // New Manifest Form State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sourceCentreId, setSourceCentreId] = useState(1);
  const [destinationCentreId, setDestinationCentreId] = useState(2);
  const [carrierName, setCarrierName] = useState('');
  const [courierDocketNo, setCourierDocketNo] = useState('');
  const [dispatchTemp, setDispatchTemp] = useState('4.0');

  const fetchManifests = async () => {
    setLoading(true);
    try {
      const data = await b2bService.getManifests();
      setManifests(data);
    } catch (err) {
      console.error('Error fetching manifests:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManifests();
  }, []);

  const handleSelectManifest = async (id) => {
    setDetailsLoading(true);
    try {
      const data = await b2bService.getManifestDetails(id);
      setSelectedManifest(data);
    } catch (err) {
      alert(err.response?.data?.message || 'Unable to load manifest details.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCreateManifest = async (e) => {
    e.preventDefault();
    try {
      const res = await b2bService.createManifest({
        sourceCentreId: Number(sourceCentreId),
        destinationCentreId: Number(destinationCentreId),
        carrierName,
        courierDocketNo,
        expectedDepartureTemp: Number(dispatchTemp)
      });
      alert(`Manifest created successfully: ${res.manifestNumber}`);
      setShowCreateModal(false);
      fetchManifests();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Error creating manifest.');
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!scanBarcode.trim() || !selectedManifest?.item1?.manifestID) return;

    setAddingItem(true);
    try {
      await b2bService.addManifestItem(selectedManifest.item1.manifestID, scanBarcode.trim());
      setScanBarcode('');
      handleSelectManifest(selectedManifest.item1.manifestID);
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Error packing specimen.');
    } finally {
      setAddingItem(false);
    }
  };

  const handleDispatch = async (manifestId) => {
    const temp = prompt('Enter Dispatch Cold-Chain Temperature (°C):', '4.0');
    if (temp === null) return;

    try {
      await b2bService.dispatchManifest(manifestId, {
        dispatchTemperature: Number(temp)
      });
      alert('Manifest dispatched successfully!');
      handleSelectManifest(manifestId);
      fetchManifests();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Error dispatching manifest.');
    }
  };

  const handleReceive = async (manifestId) => {
    const temp = prompt('Enter Arrival Cold-Chain Temperature (°C):', '4.5');
    if (temp === null) return;

    try {
      await b2bService.receiveManifest(manifestId, {
        arrivalTemperature: Number(temp),
        notes: 'Specimens received intact at destination laboratory'
      });
      alert('Manifest received at destination laboratory!');
      handleSelectManifest(manifestId);
      fetchManifests();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Error receiving manifest.');
    }
  };

  const handleVerifyItem = async (manifestItemId, status) => {
    try {
      await b2bService.verifyManifestItem({
        manifestItemId,
        status,
        rejectionReason: status === 'REJECTED' ? 'Damaged / Hemolyzed in Transit' : null
      });
      handleSelectManifest(selectedManifest.item1.manifestID);
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Error verifying item.');
    }
  };

  return (
    <div className="b2b-billing-page">
      <div className="b2b-dashboard-header">
        <div>
          <h2 className="b2b-dashboard-title">Inter-Centre Specimen Logistics &amp; Manifest Tracking</h2>
          <div className="b2b-dashboard-subtitle">
            REQ-B2B-08: Transfer manifests, chain-of-custody barcode accession packing, and cold-chain temperature auditing.
          </div>
        </div>

        <button
          className="b2b-refresh-btn"
          style={{ background: '#2563eb', color: '#ffffff' }}
          onClick={() => setShowCreateModal(true)}
        >
          + Create Transfer Manifest
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedManifest ? '1fr 1.2fr' : '1fr', gap: '20px' }}>
        {/* Manifests List */}
        <div className="b2b-table-card">
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>Loading transfer manifests...</div>
          ) : manifests.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
              No transfer manifests found. Create your first manifest above.
            </div>
          ) : (
            <table className="b2b-data-table">
              <thead>
                <tr>
                  <th>Manifest No</th>
                  <th>Route</th>
                  <th>Specimens</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {manifests.map((m) => (
                  <tr key={m.manifestID} style={{ cursor: 'pointer' }} onClick={() => handleSelectManifest(m.manifestID)}>
                    <td>
                      <strong>{m.manifestNumber}</strong>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                        {new Date(m.createdDate).toLocaleDateString('en-IN')}
                      </div>
                    </td>
                    <td>
                      {m.sourceCentreName || `Centre ${m.sourceCentreID}`} &rarr; {m.destinationCentreName || `Centre ${m.destinationCentreID}`}
                    </td>
                    <td><strong>{m.totalSpecimens}</strong> Items</td>
                    <td>
                      <span className={`b2b-badge ${m.status === 'RECEIVED' ? 'completed' : m.status === 'DISPATCHED' ? 'approved' : 'pending'}`}>
                        {m.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="b2b-action-link"
                        style={{ background: 'none', border: 'none', padding: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectManifest(m.manifestID);
                        }}
                      >
                        Inspect &rarr;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Selected Manifest Details & Specimen Scanner */}
        {selectedManifest && (
          <div className="b2b-table-card" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0 }}>Manifest: {selectedManifest.item1?.ManifestNumber}</h3>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  Status: <strong style={{ color: '#2563eb' }}>{selectedManifest.item1?.Status}</strong> &bull; Carrier: {selectedManifest.item1?.CarrierName || 'Lab Courier'}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                {selectedManifest.item1?.Status === 'DRAFT' && (
                  <button className="b2b-refresh-btn" style={{ background: '#10b981', color: '#ffffff' }} onClick={() => handleDispatch(selectedManifest.item1.ManifestID)}>
                    Dispatch Manifest
                  </button>
                )}
                {selectedManifest.item1?.Status === 'DISPATCHED' && (
                  <button className="b2b-refresh-btn" style={{ background: '#3b82f6', color: '#ffffff' }} onClick={() => handleReceive(selectedManifest.item1.ManifestID)}>
                    Receive at Lab
                  </button>
                )}
              </div>
            </div>

            {/* Specimen Packing Barcode Input */}
            {selectedManifest.item1?.Status === 'DRAFT' && (
              <form onSubmit={handleAddItem} style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                <input
                  type="text"
                  className="b2b-filter-input"
                  style={{ flex: 1 }}
                  placeholder="Scan or enter specimen barcode (e.g. BC-10023)..."
                  value={scanBarcode}
                  onChange={(e) => setScanBarcode(e.target.value)}
                  autoFocus
                />
                <button type="submit" className="b2b-refresh-btn" disabled={addingItem}>
                  {addingItem ? 'Packing...' : '+ Pack Specimen'}
                </button>
              </form>
            )}

            <h4 style={{ margin: '0 0 12px 0' }}>Packed Specimens ({selectedManifest.item2?.length || 0})</h4>
            <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
              <table className="b2b-data-table">
                <thead>
                  <tr>
                    <th>Barcode</th>
                    <th>Patient</th>
                    <th>Investigation</th>
                    <th>Status</th>
                    <th>Verification</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedManifest.item2?.map((it, idx) => (
                    <tr key={idx}>
                      <td><code>{it.barcodeNo}</code></td>
                      <td>{it.patientName}</td>
                      <td>{it.investigationName}</td>
                      <td>
                        <span className={`b2b-badge ${it.status === 'ACCEPTED' ? 'completed' : it.status === 'REJECTED' ? 'high' : 'pending'}`}>
                          {it.status}
                        </span>
                      </td>
                      <td>
                        {selectedManifest.item1?.Status === 'RECEIVED' && it.status === 'PACKED' && (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              style={{ padding: '2px 6px', fontSize: '11px', background: '#dcfce7', color: '#15803d', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                              onClick={() => handleVerifyItem(it.manifestItemId, 'ACCEPTED')}
                            >
                              Accept
                            </button>
                            <button
                              style={{ padding: '2px 6px', fontSize: '11px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                              onClick={() => handleVerifyItem(it.manifestItemId, 'REJECTED')}
                            >
                              Reject
                            </button>
                          </div>
                        )}
                        {it.status !== 'PACKED' && (
                          <span style={{ fontSize: '11px', color: '#64748b' }}>Verified</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="b2b-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="b2b-modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="b2b-modal-header">
              <h3 style={{ margin: 0 }}>Create Specimen Transfer Manifest</h3>
              <button className="b2b-close-btn" onClick={() => setShowCreateModal(false)}>&times;</button>
            </div>

            <form onSubmit={handleCreateManifest}>
              <div className="b2b-form-group">
                <label className="b2b-form-label">Source Collection Centre ID</label>
                <input
                  type="number"
                  className="b2b-form-input"
                  value={sourceCentreId}
                  onChange={(e) => setSourceCentreId(e.target.value)}
                  required
                />
              </div>

              <div className="b2b-form-group">
                <label className="b2b-form-label">Destination Processing Lab Centre ID</label>
                <input
                  type="number"
                  className="b2b-form-input"
                  value={destinationCentreId}
                  onChange={(e) => setDestinationCentreId(e.target.value)}
                  required
                />
              </div>

              <div className="b2b-form-group">
                <label className="b2b-form-label">Carrier / Logistics Agent Name</label>
                <input
                  type="text"
                  className="b2b-form-input"
                  placeholder="e.g. MedSpeed Couriers"
                  value={carrierName}
                  onChange={(e) => setCarrierName(e.target.value)}
                />
              </div>

              <div className="b2b-form-group">
                <label className="b2b-form-label">Tracking / Docket Number</label>
                <input
                  type="text"
                  className="b2b-form-input"
                  placeholder="e.g. DKT-998822"
                  value={courierDocketNo}
                  onChange={(e) => setCourierDocketNo(e.target.value)}
                />
              </div>

              <div className="b2b-form-group">
                <label className="b2b-form-label">Cold-Chain Temperature Requirement (°C)</label>
                <input
                  type="number"
                  step="0.1"
                  className="b2b-form-input"
                  value={dispatchTemp}
                  onChange={(e) => setDispatchTemp(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="b2b-submit-btn" style={{ background: '#2563eb' }}>
                Generate Manifest Number
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default B2bSpecimenLogisticsPage;
